<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<!--
  Reusable column toggle panel: button + popover overlay to
  toggle column visibility and reorder columns via drag & drop
  or arrow buttons. The "name" column is always locked.
-->

<template>
  <span class="column-toggle-inline">
    <PrimeButton
      type="button"
      icon="ri-settings-3-line"
      size="small"
      severity="secondary"
      text
      rounded
      v-tooltip.left="$t('column.customize')"
      @click="toggle"
    />
    <PrimePopover ref="overlay">
      <div class="column-toggle-panel">
        <div class="column-toggle-title">{{ $t("column.customize") }}</div>
        <PrimeDivider />
        <!-- Search bar -->
        <div class="column-toggle-search">
          <PrimeIconField>
            <PrimeInputIcon class="ri-search-line" />
            <PrimeInputText
              v-model="searchQuery"
              :placeholder="$t('column.search')"
              size="small"
              fluid
            />
            <PrimeInputIcon
              v-if="searchQuery"
              class="ri-close-line search-clear-icon"
              @click="searchQuery = ''"
            />
          </PrimeIconField>
        </div>

        <!-- Search results (only when searching) -->
        <div v-if="isSearching" class="column-toggle-section">
          <div class="column-toggle-section-header">
            {{ $t("column.searchResults") }}
          </div>
          <div class="column-toggle-list">
            <div
              v-for="col in searchResults"
              :key="'search-' + col.key"
              class="column-toggle-item"
            >
              <label
                :for="'search-col-' + col.key"
                class="column-toggle-label"
                >{{ col.label }}</label
              >
              <PrimeToggleSwitch
                :modelValue="col.visible"
                :inputId="'search-col-' + col.key"
                @update:modelValue="onToggleVisibility(col, $event)"
              />
            </div>
          </div>
        </div>

        <!-- Displayed columns (always visible) -->
        <div class="column-toggle-section">
          <div class="column-toggle-section-header">
            {{ $t("column.displayed") }}
          </div>
          <div class="column-toggle-list">
            <div
              v-for="(col, index) in visibleColumns"
              :key="col.key"
              class="column-toggle-item"
              :class="{ 'drag-over': dragOverIndex === index }"
              :draggable="!isSmallScreen"
              @dragstart="!isSmallScreen && onDragStart(index, $event)"
              @dragover.prevent="!isSmallScreen && onDragOver(index)"
              @dragleave="!isSmallScreen && onDragLeave()"
              @drop="!isSmallScreen && onDrop(index)"
              @dragend="!isSmallScreen && onDragEnd()"
            >
              <i v-if="!isSmallScreen" class="ri-draggable drag-handle" />
              <label :for="'col-' + col.key" class="column-toggle-label">{{
                col.label
              }}</label>
              <div class="column-toggle-arrows">
                <PrimeButton
                  type="button"
                  icon="ri-arrow-up-s-line"
                  size="small"
                  severity="secondary"
                  text
                  rounded
                  :disabled="index === 0"
                  @click="moveColumn(index, -1)"
                />
                <PrimeButton
                  type="button"
                  icon="ri-arrow-down-s-line"
                  size="small"
                  severity="secondary"
                  text
                  rounded
                  :disabled="index === visibleColumns.length - 1"
                  @click="moveColumn(index, 1)"
                />
              </div>
              <PrimeToggleSwitch
                :modelValue="col.visible"
                :inputId="'col-' + col.key"
                @update:modelValue="onToggleVisibility(col, $event)"
              />
            </div>
          </div>
        </div>

        <!-- Hidden columns (only when not searching) -->
        <div
          v-if="!isSearching && hiddenColumns.length > 0"
          class="column-toggle-section"
        >
          <div class="column-toggle-section-header">
            {{ $t("column.hidden") }}
          </div>
          <div class="column-toggle-list">
            <div
              v-for="col in hiddenColumns"
              :key="col.key"
              class="column-toggle-item"
            >
              <label
                :for="'hidden-col-' + col.key"
                class="column-toggle-label"
                >{{ col.label }}</label
              >
              <PrimeToggleSwitch
                :modelValue="col.visible"
                :inputId="'hidden-col-' + col.key"
                @update:modelValue="onToggleVisibility(col, $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </PrimePopover>
  </span>
</template>

<script>
import Button from "primevue/button";
import ToggleSwitch from "primevue/toggleswitch";
import Popover from "primevue/popover";
import Tooltip from "primevue/tooltip";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Divider from "primevue/divider";

export default {
  name: "ColumnTogglePanel",
  components: {
    PrimeButton: Button,
    PrimeToggleSwitch: ToggleSwitch,
    PrimePopover: Popover,
    PrimeInputText: InputText,
    PrimeIconField: IconField,
    PrimeInputIcon: InputIcon,
    PrimeDivider: Divider,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    columns: {
      type: Array,
      required: true,
    },
    isSmallScreen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:columns"],
  data() {
    return {
      dragIndex: null,
      dragOverIndex: null,
      searchQuery: "",
    };
  },
  computed: {
    toggleableColumns() {
      return this.columns.filter((col) => col.key !== "name");
    },
    visibleColumns() {
      return this.toggleableColumns.filter((col) => col.visible);
    },
    hiddenColumns() {
      return this.toggleableColumns.filter((col) => !col.visible);
    },
    isSearching() {
      return this.searchQuery.trim().length > 0;
    },
    searchResults() {
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) {
        return [];
      }
      return this.toggleableColumns.filter(
        (col) => col.label && col.label.toLowerCase().includes(query),
      );
    },
  },
  methods: {
    toggle(event) {
      this.$refs.overlay.toggle(event);
    },
    onToggleVisibility(col, value) {
      col.visible = value;
    },
    moveColumn(visibleIndex, direction) {
      const col = this.visibleColumns[visibleIndex];
      const targetVisibleIndex = visibleIndex + direction;
      const targetCol = this.visibleColumns[targetVisibleIndex];

      const realIndex = this.columns.indexOf(col);
      const targetRealIndex = this.columns.indexOf(targetCol);

      const updated = [...this.columns];
      updated.splice(realIndex, 1);
      updated.splice(targetRealIndex, 0, col);
      this.$emit("update:columns", updated);
    },

    // Drag & drop
    onDragStart(index, event) {
      this.dragIndex = index;
      event.dataTransfer.effectAllowed = "move";
    },
    onDragOver(index) {
      this.dragOverIndex = index;
    },
    onDragLeave() {
      this.dragOverIndex = null;
    },
    onDrop(targetIndex) {
      this.dragOverIndex = null;
      if (this.dragIndex === null || this.dragIndex === targetIndex) {
        return;
      }

      const dragCol = this.visibleColumns[this.dragIndex];
      const targetCol = this.visibleColumns[targetIndex];
      const dragRealIndex = this.columns.indexOf(dragCol);
      const targetRealIndex = this.columns.indexOf(targetCol);

      const updated = [...this.columns];
      updated.splice(dragRealIndex, 1);
      updated.splice(targetRealIndex, 0, dragCol);
      this.$emit("update:columns", updated);
    },
    onDragEnd() {
      this.dragIndex = null;
      this.dragOverIndex = null;
    },
  },
};
</script>

<style scoped lang="scss">
.column-toggle-inline {
  display: inline-flex;
  align-items: center;
}

.column-toggle-panel {
  display: flex;
  flex-direction: column;
  min-width: 220px;
}

.column-toggle-title {
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.5rem 0.5rem 0;
}

.column-toggle-panel :deep(.p-divider) {
  margin: 0.5rem 0;
}

.column-toggle-search {
  padding: 0.5rem;
  border-bottom: 1px solid var(--p-surface-border);

  :deep(.search-clear-icon) {
    cursor: pointer;
    pointer-events: auto;
    border-radius: 50%;

    &:hover {
      background: var(--p-surface-hover);
    }
  }
}

.column-toggle-section {
  &:not(:last-child) {
    border-bottom: 1px solid var(--p-surface-border);
  }
}

.column-toggle-section-header {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--p-text-muted-color);
  padding: 0.5rem 0.5rem 0.25rem;
}

.column-toggle-list {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.25rem;
}

.column-toggle-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.375rem;
  transition: background 0.15s;

  &:hover {
    background: var(--p-surface-hover);
  }

  &.drag-over {
    border-top: 2px solid var(--p-primary-color);
  }
}

.drag-handle {
  cursor: grab;
  color: var(--p-text-muted-color);
  font-size: 1rem;

  &:active {
    cursor: grabbing;
  }
}

.column-toggle-label {
  cursor: pointer;
  font-size: 0.875rem;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.column-toggle-arrows {
  display: flex;
  margin-left: auto;

  :deep(.p-button) {
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
  }
}
</style>
